// src/components/auth/MfaEnrollGate.jsx
// Forced one-time enrollment: after password sign-in, an agent with no
// second factor must bind their cell number before the dashboard renders.
// Firebase enforces the factor at every future fresh login; we just enroll.
import { useEffect, useRef, useState } from "react";
import { RecaptchaVerifier, getAuth, signOut } from "firebase/auth";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import CodeInput from "./CodeInput";
import { formatPhoneAsTyped } from "../../utils/checkinFlow";
import { updateUserProfile } from "../../utils/firebasefunctions";
import {
  toE164US,
  startEnrollment,
  finishEnrollment,
  mfaErrorMessage,
} from "../../utils/mfa";

export default function MfaEnrollGate({ onEnrolled }) {
  const [{ user }] = useStateValue();
  const auth = getAuth();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [busy, setBusy] = useState(false);
  // True when Firebase says MFA isn't switched on for this project yet
  // (auth/operation-not-allowed). Without an escape hatch that state would
  // lock the whole team out, so we offer "continue for now" and nag Henry.
  const [setupIncomplete, setSetupIncomplete] = useState(false);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    // Invisible reCAPTCHA anchored to the Send button's container.
    recaptchaRef.current = new RecaptchaVerifier(auth, "mfa-recaptcha", {
      size: "invisible",
    });
    return () => {
      try { recaptchaRef.current && recaptchaRef.current.clear(); } catch { /* fine */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendCode = async () => {
    const e164 = toE164US(phone);
    if (!e164) {
      toast.error("Use your 10 digit cell number.");
      return;
    }
    setBusy(true);
    try {
      const id = await startEnrollment(auth, auth.currentUser, e164, recaptchaRef.current);
      setVerificationId(id);
      toast.success("Code texted to your phone.");
    } catch (err) {
      console.error("MFA enrollment send failed:", err);
      toast.error(mfaErrorMessage(err));
      if (err?.code === "auth/operation-not-allowed") setSetupIncomplete(true);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (code.replace(/\D/g, "").length < 6) {
      toast.error("Enter the 6 digit code from the text.");
      return;
    }
    setBusy(true);
    try {
      await finishEnrollment(auth.currentUser, verificationId, code.trim());
      // Bookkeeping only; Firebase itself enforces the factor from now on.
      if (user?.id) {
        updateUserProfile(user.id, { mfaEnrolledAt: Date.now() }).catch(() => {});
      }
      toast.success("Two-step verification is on.");
      onEnrolled && onEnrolled();
    } catch (err) {
      console.error("MFA enrollment confirm failed:", err);
      toast.error(mfaErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        <h1 className="text-xl font-bold text-slate-900 mb-1">Secure your account</h1>
        <p className="text-sm text-slate-500 mb-6">
          Add your cell number. Each time you log in on a new device, we text you a
          6 digit code to confirm it's really you.
        </p>

        {!verificationId ? (
          <>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              Cell number
            </label>
            <input
              value={phone}
              onChange={(e) => setPhone(formatPhoneAsTyped(e.target.value))}
              inputMode="tel"
              placeholder="(555) 201-8890"
              className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-slate-800 mb-4"
            />
            <div id="mfa-recaptcha" />
            <button
              onClick={sendCode}
              disabled={busy}
              className="w-full bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-semibold rounded-lg py-3"
            >
              {busy ? "Sending..." : "Text me a code"}
            </button>
          </>
        ) : (
          <>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              6 digit code
            </label>
            <CodeInput value={code} onChange={setCode} onSubmit={confirm} />
            <button
              onClick={confirm}
              disabled={busy}
              className="w-full bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-semibold rounded-lg py-3"
            >
              {busy ? "Checking..." : "Turn on two-step verification"}
            </button>
            <button
              onClick={() => { setVerificationId(""); setCode(""); }}
              className="w-full text-sm text-slate-500 mt-3"
            >
              Wrong number? Go back
            </button>
          </>
        )}

        {setupIncomplete && (
          <button
            onClick={() => {
              // Remember for this browser session so reloads don't nag —
              // only reachable when Firebase says MFA isn't enabled yet.
              try { sessionStorage.setItem("mfaSetupIncompleteBypass", "1"); } catch { /* fine */ }
              onEnrolled && onEnrolled();
            }}
            className="w-full text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg py-2 mt-4"
          >
            Two-step setup is not finished in Firebase. Continue for now.
          </button>
        )}

        <button
          onClick={() => signOut(auth)}
          className="w-full text-xs text-slate-400 hover:text-slate-600 mt-6"
        >
          Log out instead
        </button>
      </div>
    </div>
  );
}
